/**
 * Shared dashboard types for admin panel
 * 
 * This file contains all dashboard-related types and utilities used across
 * both frontend and backend components.
 */
import { Layout } from "react-grid-layout";
import { BarChart, HeroNumberWithFields, PieChart, Map } from "../survey";

// =============================================================================
// CORE ENTITIES
// =============================================================================

/**
 * Dashboard entity representing a dashboard configuration
 */
export interface Dashboard {
  id: string;
  title: string;
  description?: string;
  created_by: string;
  created_at: number;
  updated_at: number;
  view_only?: boolean;
}

/**
 * Supported dashboard component types with description fields
 */
export type DashboardMetric = HeroNumberWithFields & { description?: string }
export type DashboardBarChart = BarChart & { description?: string, xAxisLabel?: string, yAxisLabel?: string }
export type DashboardPieChart = PieChart & { description?: string }
export type SupportedDashboardComponent = (DashboardMetric | DashboardBarChart | DashboardPieChart | Map)

export type StyledDashboardComponent = {
  config: SupportedDashboardComponent;
  style?: Layout;
}

/**
 * Dashboard component with a unique identifier
 */
export interface DashboardComponentWithId {
  id: string;
  config: SupportedDashboardComponent;
  style?: Layout;
}

// =============================================================================
// QUERY RESULT TYPES
// =============================================================================

/**
 * Standard timed query result structure
 */
export interface TimedQueryResult {
  rows: Array<any>;
  rowCount: number | null;
  executionTimeMs: number;
  lastUpdated: number;
}

/**
 * Dashboard component translation result with summary key
 */
export type TranslatedDashboardComponentResultWithKey = TimedQueryResult & {
  summaryKey: string;
}

// =============================================================================
// STAGED COMPONENT TYPES
// =============================================================================

/**
 * Staged component used in dashboard builder
 * Contains temporary state and results for dashboard components before saving
 */
export interface StagedComponent {
  // Stable UUID for React keys and component tracking as a unique identifier
  // to a staged component before it gets saved to the database
  id: string;
  config: SupportedDashboardComponent;
  results?: TranslatedComponentResult;
  isLoading?: boolean;
  error?: string;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Creates a new staged component with a generated id
 */
export function createStagedComponent(
  component: SupportedDashboardComponent,
  results?: TranslatedComponentResult
): StagedComponent {
  return {
    id: crypto.randomUUID(),
    config: component,
    results,
    isLoading: false,
    error: undefined
  };
}

/**
 * Updates an existing staged component while preserving the id
 */
export function updateStagedComponent(
  stagedComponent: StagedComponent,
  updates: Partial<Omit<StagedComponent, 'id'>>
): StagedComponent {
  return {
    ...stagedComponent,
    ...updates
  };
}

/**
 * Creates multiple staged components from an array of dashboard components
 */
export function createStagedComponents(
  components: SupportedDashboardComponent[]
): StagedComponent[] {
  return components.map(component => createStagedComponent(component));
}

/**
 * Type guard to check if an object is a valid StagedComponent
 */
export function isStagedComponent(obj: any): obj is StagedComponent {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.id === 'string' &&
    obj.component &&
    typeof obj.component === 'object' &&
    typeof obj.component.kind === 'string'
  );
}

// =============================================================================
// SERVICE RESULT TYPES (DTOs)
// =============================================================================

export interface TranslatedComponentResult {
  summaryKey: string;
  data: any;
  timing?: number;
  rowCount?: number;
}

/**
 * Result type for getting all dashboards
 */
export interface GetDashboardsResult {
  dashboards: Array<Dashboard>;
}

/**
 * Result type for getting a dashboard by ID with components
 */
export interface GetDashboardByIdResult {
  id: string;
  title: string;
  description: string;
  created_by: string;
  created_at: number;
  updated_at: number;
  components: Array<DashboardComponentWithId>;
  view_only: boolean;
}

/**
 * Result type for translating dashboard components
 */
export interface TranslateDashboardComponentResult {
  result: TranslatedDashboardComponentResultWithKey;
}

/**
 * Result type for creating a new dashboard
 */
export interface CreateDashboardResult {
  id: string;
}

/**
 * Result type for updating a dashboard
 */
export interface UpdateDashboardResult {
  id: string;
}

/**
 * Result type for deleting a dashboard
 */
export interface DeleteDashboardResult { }

/**
 * Result type for logging a dashboard download
 */
export interface LogDashboardDownloadResult { }